import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import { Lock, Mail, User, Loader2, Check, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

interface RegisterFormProps {
  onToggle: () => void;
  onAlreadyHaveAccount: () => void;
}

export default function RegisterForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
  });
  const [error, setError] = useState('');
  const { register, isLoading } = useAuth();
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const validatePassword = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (name === 'password') {
      setPasswordStrength(validatePassword(value));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!acceptedTerms) {
      setError(t('register.acceptTermsError'));
      return;
    }
    setError('');

    try {
      const response = await register(formData.email, formData.password, formData.name, acceptedTerms);

      if (!response?.data?.registerResponse) {
        throw new Error('Invalid registration response');
      }

      navigate('/app/auth/validate-account', {
        state: {
          data: {
            email: formData.email,
          },
          redirectTo: '/app/auth/login'
        }
      });

    } catch (err) {
      setError(err instanceof Error ? err.message : t('register.genericError'));
    }
  };

  const inputClasses = "w-full bg-white/10 border border-gray-500 text-white rounded-lg py-3 px-11 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200";
  const iconClasses = "absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 transition-colors duration-200";

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      onSubmit={handleSubmit}
      className="space-y-6"
    >
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="p-3 bg-red-500/20 border border-red-500/50 text-red-200 rounded-lg flex items-center gap-2"
          >
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="space-y-4">
        <div className="relative group">
          <User className={iconClasses} />
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`${inputClasses} group-hover:border-blue-400`}
            placeholder={t('register.fullName')}
            required
            disabled={isLoading}
          />
        </div>

        <div className="relative group">
          <Mail className={iconClasses} />
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={`${inputClasses} group-hover:border-blue-400`}
            placeholder={t('register.email')}
            required
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <div className="relative group">
            <Lock className={iconClasses} />
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              className={`${inputClasses} group-hover:border-blue-400`}
              placeholder={t('register.password')}
              required
              disabled={isLoading}
            />
          </div>

          {formData.password && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="space-y-1"
            >
              <div className="flex gap-1">
                {[...Array(4)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="h-1 flex-1 rounded-full"
                    style={{
                      backgroundColor: i < passwordStrength ?
                        passwordStrength <= 2 ? 'rgb(239 68 68)' :
                          passwordStrength === 3 ? 'rgb(234 179 8)' :
                            'rgb(34 197 94)' : 'rgb(75 85 99)'
                    }}
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: i * 0.1 }}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-400">
                {passwordStrength === 0 && t('register.passwordWeak')}
                {passwordStrength === 1 && t('register.passwordFair')}
                {passwordStrength === 2 && t('register.passwordGood')}
                {passwordStrength === 3 && t('register.passwordStrong')}
                {passwordStrength === 4 && t('register.passwordExcellent')}
              </p>
            </motion.div>
          )}
        </div>
      </div>

      <motion.div
        className="flex items-center space-x-2"
        whileTap={{ scale: 0.98 }}
      >
        <input
          type="checkbox"
          id="terms"
          checked={acceptedTerms}
          onChange={(e) => setAcceptedTerms(e.target.checked)}
          className="rounded border-gray-500 bg-white/10 text-blue-500 focus:ring-blue-500 transition-colors duration-200"
          disabled={isLoading}
        />
        <label htmlFor="terms" className="text-gray-400 text-sm">
          {t('register.accept')}{' '}
          <a
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-300 underline transition-colors duration-200"
          >
            {t('register.terms')}
          </a>
        </label>
      </motion.div>

      <motion.button
        type="submit"
        disabled={isLoading}
        className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-lg font-semibold
                 hover:from-blue-600 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-blue-500/25
                 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.98 }}
      >
        <span className="flex items-center justify-center gap-2">
          {isLoading ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Check className="w-5 h-5" />
          )}
          {isLoading ? t('register.loading') : t('register.submit')}
        </span>
      </motion.button>

      <motion.p
        className="text-center text-gray-400 text-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {t('register.haveAccount')}{' '}
        <button
          type="button"
          onClick={() => navigate('/auth/login')}
          disabled={isLoading}
          className="text-blue-400 hover:text-blue-300 transition-colors duration-200 font-medium"
        >
          {t('register.signIn')}
        </button>
      </motion.p>
    </motion.form>
  );
}